import React from 'react';

/**
 * "Powered by CrewSync Pro" brand attribution banner.
 *
 * Strategic placement: this is where the platform brand IS shown loud — on
 * customer-facing public pages (estimate approval, invoice payment, document
 * signing, public reviews, project gallery). These pages aren't subject to
 * Gmail's classifier, so we can amplify the brand here without the
 * deliverability cost.
 *
 * Three variants:
 *  - "subtle"  → tiny grey footer line (default)
 *  - "footer"  → full-width banded footer with CTA
 *  - "badge"   → corner badge for header areas
 *
 * Don't overuse it — one footer + (optionally) one corner badge per page is
 * enough. Doubling up = looks salesy and undermines the customer's trust in
 * the tenant.
 */
export default function PoweredByCrewSync({ variant = 'subtle', tenantName = '' }) {
  if (variant === 'badge') {
    return (
      <a
        href="https://crewsyncpro.com"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-white/10 transition-colors"
        data-testid="powered-by-crewsync-badge"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
        <span>Powered by <strong className="text-zinc-200">CrewSync Pro</strong></span>
      </a>
    );
  }

  if (variant === 'footer') {
    // Full-width banded CTA — used at the very bottom of the customer page
    // AFTER they've engaged with the content (read estimate, signed doc,
    // paid invoice). High-intent moment to convert curious viewers into
    // CrewSync trial signups.
    return (
      <div
        className="mt-12 -mx-4 px-4 py-8 sm:px-6 bg-gradient-to-br from-zinc-900 to-black border-t border-zinc-800 text-center"
        data-testid="powered-by-crewsync-footer"
      >
        <p className="text-zinc-400 text-sm mb-1">
          {tenantName
            ? `${tenantName} runs their business on`
            : 'Powered by'}
        </p>
        <a
          href="https://crewsyncpro.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-2xl font-bold text-primary hover:text-primary/80 transition-colors"
        >
          CrewSync Pro
        </a>
        <p className="text-zinc-500 text-xs mt-3 max-w-md mx-auto">
          The complete platform for field-service contractors — estimates,
          jobs, invoicing, and crew management.
        </p>
        <a
          href="https://crewsyncpro.com?ref=customer-page"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 text-xs text-zinc-400 hover:text-primary underline-offset-4 hover:underline"
        >
          Want a tool like this for your business? Try free →
        </a>
      </div>
    );
  }

  // Default "subtle" — single greyscale line, used inside compact areas.
  return (
    <div className="text-center text-xs text-zinc-500 py-3" data-testid="powered-by-crewsync-subtle">
      Powered by{' '}
      <a
        href="https://crewsyncpro.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-zinc-400 hover:text-primary transition-colors font-medium"
      >
        CrewSync Pro
      </a>
    </div>
  );
}
